import "./RemainingPagesResponsive.css";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN } from "../api";
import AdminLayout from "../components/AdminLayout";
import {
  CheckCheck, ListFilter, ClipboardList, FileText, CheckCircle2, ShieldAlert, MessageCircle,
} from "lucide-react";



const TYPE_META = {
  request_new: { icon: ClipboardList, cls: "bg-blue-100 text-blue-600" },
  quote_sent: { icon: FileText, cls: "bg-teal-100 text-teal-600" },
  quote_approved: { icon: CheckCircle2, cls: "bg-green-100 text-green-600" },
  quote_declined: { icon: ShieldAlert, cls: "bg-red-100 text-red-600" },
  comment_new: { icon: MessageCircle, cls: "bg-indigo-100 text-indigo-600" },
};

const timeAgo = (d) => {
  const m = Math.round((Date.now() - new Date(d)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  return `${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? "" : "s"} ago`;
};

const FILTERS = ["All", "Unread", "High Priority"];

export default function AdminNotifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const socketRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      /* keep prior state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // live updates: same "admin" room already used elsewhere
  useEffect(() => {
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"], auth: { token: localStorage.getItem("token") } });
    socketRef.current = socket;
    socket.emit("join", "admin");
    socket.on("notification:new", (n) => setItems((prev) => [n, ...prev]));
    return () => socket.disconnect();
  }, []);

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
    try { await api.patch(`/notifications/${id}/read`); } catch { /* optimistic */ }
  };

  const dismiss = async (id) => {
    setItems((prev) => prev.filter((n) => n._id !== id));
    try { await api.delete(`/notifications/${id}`); } catch { /* optimistic */ }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try { await api.patch("/notifications/read-all"); } catch { /* optimistic */ }
  };

  const openNotification = (n) => {
    if (!n.read) markRead(n._id);
    if (n.link) navigate(n.link);
  };

  const filtered = useMemo(() => {
    if (filter === "Unread") return items.filter((n) => !n.read);
    if (filter === "High Priority") return items.filter((n) => n.priority === "high");
    return items;
  }, [items, filter]);

  return (
    <AdminLayout>
      <main className="p-3 sm:p-6 agp-rem-page">
        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="flex items-start justify-between flex-wrap gap-3 p-6 border-b border-gray-100">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900">Notifications</h1>
              <p className="text-sm text-slate-500 mt-1">Showing latest notifications for your account.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={markAllRead}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <CheckCheck size={16} /> Mark all as read
              </button>
              <div className="relative">
                <button onClick={() => setShowFilterMenu((s) => !s)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
                  <ListFilter size={16} /> Filter
                </button>
                {showFilterMenu && (
                  <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-10 w-44">
                    {FILTERS.map((f) => (
                      <button key={f} onClick={() => { setFilter(f); setShowFilterMenu(false); }}
                        className={`w-full text-left px-4 py-2 text-sm ${filter === f ? "font-bold text-slate-900 bg-slate-50" : "text-slate-600 hover:bg-slate-50"}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {loading && <p className="text-center text-slate-400 py-12">Loading notifications…</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-center text-slate-400 py-12">Nothing here.</p>
            )}
            {!loading && filtered.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.comment_new;
              const Icon = meta.icon;
              return (
                <div key={n._id}
                  className={`rounded-xl border p-4 flex items-start gap-4 ${!n.read ? "border-l-4 border-l-blue-700 border-y border-r border-gray-100 bg-blue-50/20" : "border-gray-100"
                    }`}>
                  <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ${meta.cls}`}>
                    <Icon size={18} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <button onClick={() => openNotification(n)} className="text-left">
                      <div className="font-bold text-slate-800">{n.title}</div>
                    </button>
                    <p className="text-sm text-slate-500 mt-1">{n.message}</p>
                    {n.priority === "high" && (
                      <span className="inline-block mt-2 text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-600">
                        High Priority
                      </span>
                    )}
                    {!n.read && (
                      <div className="flex items-center gap-4 mt-2 text-sm font-semibold">
                        <button onClick={() => openNotification(n)} className="text-blue-700 hover:text-blue-800">View Detail</button>
                        <button onClick={() => markRead(n._id)} className="text-slate-500 hover:text-slate-700">Mark as Read</button>
                        <button onClick={() => dismiss(n._id)} className="text-slate-400 hover:text-slate-600">Dismiss</button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-xs text-slate-400">{timeAgo(n.createdAt)}</span>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-blue-700" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </AdminLayout>
  );
}
